import * as React from 'react';
import { Provider } from 'react-redux';

import { MsalProvider } from '@azure/msal-react';
import { MantineColorsTuple, MantineProvider, createTheme } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { createRoot } from 'react-dom/client';
import { PersistGate } from 'redux-persist/integration/react';
import { VisynAppProvider } from 'visyn_core/app';

// Import Rubik font (self-hosted)
import '@fontsource/rubik/400.css'; // Regular
import '@fontsource/rubik/500.css'; // Medium
import '@fontsource/rubik/600.css'; // Semibold

import { App } from './App';
import { msalInstance } from './auth/azureConfig';
import { LoadingContainer } from './common/LoadingContainer';
import { persistor, store } from './store/store';

// CSS Import Order (IMPORTANT: order matters for specificity)
// 1. Third-party styles first (lowest priority)
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/carousel/styles.css';
import 'react-mosaic-component/react-mosaic-component.css';
import 'visyn_core/scss/main.scss';

// 2. Design tokens (medium priority - can be overridden by custom styles)
import './theme/design-tokens.css';

// 3. Custom styles last (highest priority - overrides everything)
import './index.css';

// Brand green color scale (based on #00A650)
const brand: MantineColorsTuple = ['#e6f7ed', '#b3e6cb', '#80d5a9', '#4dc487', '#1ab365', '#00A650', '#008741', '#006832', '#004923', '#002a14'];

// Create Mantine theme with Rubik font and brand colors
const theme = createTheme({
  fontFamily: "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  headings: {
    fontFamily: "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  colors: {
    brand,
  },
  primaryColor: 'brand',
  primaryShade: 5,
});

// Function to render the app
const renderApp = () => {
  createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <MantineProvider theme={theme}>
          <ModalsProvider>
            <Notifications position="top-center" />
            <VisynAppProvider appName="ImmunoSense" mantineNotificationsProviderProps={{ position: 'top-center' }}>
              <PersistGate loading={<LoadingContainer height={500} />} persistor={persistor}>
                <Provider store={store}>
                  <App />
                </Provider>
              </PersistGate>
            </VisynAppProvider>
          </ModalsProvider>
        </MantineProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
};
// Initialize MSAL and handle redirect before rendering the app
msalInstance
  .initialize()
  .then(() => {
    console.log('MSAL initialized, handling redirect promise...');
    return msalInstance.handleRedirectPromise();
  })
  .then((response) => {
    console.log('Initial redirect promise response:', response);
    if (response && response.account) {
      console.log('Setting active account from initial redirect:', response.account);
      msalInstance.setActiveAccount(response.account);
    } else {
      // Check for existing accounts
      const accounts = msalInstance.getAllAccounts();
      console.log('Existing accounts found during initialization:', accounts);
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0] || null);
      }
    }

    renderApp();
  })
  .catch((error) => {
    console.error('MSAL initialization or redirect handling failed:', error);
    // Still render the app even if there's an error - the app will handle auth errors
    renderApp();
  });
